import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRoom } from "./useRoom";
import { Controls } from "./controls/Controls";
import { RootState } from "store/reducers";
import { Settings } from "./settings/Settings";
import { ErrorAlert } from "components/shared/error/ErrorAlert";
import { removeErrorNotification } from "store/errors/slice";
import { ErrorBar } from "components/shared/error/ErrorBar";
import { SharingScreenLayout } from "./layouts/SharingScreenLayout";
import { PresentationLayout } from "./layouts/PresentationLayout";
import { GridLayout } from "./layouts/GridLayout";
import styles from "./Room.module.css";
import { useSyncRoom } from "./useSyncRoom";
import { RoomAssignmentBar } from "./RoomAssignmentBar/RoomAssignmentBar";
import { appRegx, isHostingApp, twilioSyncEnabled } from "utils";
import { LocalParticipantLayout } from "./layouts/LocalParticipantLayout";
import SyncClient from "twilio-sync";
import { MobileChat } from "./chat/mobileChat/MobileChat";
import {isMobile} from "react-device-detect";

interface RoomProps {
  roomUid: string;
  syncClient?: SyncClient;
}

export const Room: React.FC<RoomProps> = ({ roomUid, syncClient }) => {
  const dispatch = useDispatch();
  const { error } = useSelector((s: RootState) => s.errors);
  const { participants } = useSelector((s: RootState) => s.remoteParticipants);
  const { privateChatEnabled } = useSelector((s: RootState) => s.user);
  const { participantsInArea } = useSyncRoom(roomUid, syncClient);
  const { room } = useRoom(roomUid);
  const { showLocalTrackControls, shareScreen, showOnlyLocalVideo } = useSelector(
    (s: RootState) => s.localTracks
  );
  const { showChat } = useSelector((s: RootState) => s.chatMessages);

  return (
    <div className={styles["room"]}>
      <ErrorBar show={!!error}>
        <ErrorAlert
          error={error}
          dismiss={() => dispatch(removeErrorNotification())}
        />
      </ErrorBar>
      {twilioSyncEnabled && <RoomAssignmentBar />}
      {room && showOnlyLocalVideo && (
        <LocalParticipantLayout
          room={room}
          participantsInArea={participantsInArea}
        />
      )}
      {room && shareScreen && !showOnlyLocalVideo && (
        <SharingScreenLayout
          room={room}
          participantsInArea={participantsInArea}
        />
      )}
      {room && !shareScreen && !showOnlyLocalVideo && (
        <>
          {participants.some(isHostingApp) ? (
            <PresentationLayout
              key="live-feed-layout"
              room={room}
              participantsInArea={participantsInArea}
              findPredicate={isHostingApp}
              filterCallback={(p) => !appRegx.test(p.identity)}
            />
          ) : participants.some((p) => p.isSharingScreen) ? (
            <PresentationLayout
              key="presentation-layout"
              room={room}
              participantsInArea={participantsInArea}
              findPredicate={(p) => p.isSharingScreen}
            />
          ) : (
            <>
              <GridLayout
                key="grid-layout"
                room={room}
                participantsInArea={participantsInArea}
              />
            </>
          )}
        </>
      )}
      <>
        <Controls room={room} />
        {showLocalTrackControls && <Settings />}
        {privateChatEnabled && isMobile && showChat && 
          <MobileChat />
        }
      </>
    </div>
  );
};
